// Polymorphic principal output matches checkpoint approver wire shape;
// merging user/role/group at the picker boundary keeps form schema flat.
import { useState, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { Users, Shield, X, User } from "lucide-react";
import { formatUserName } from "@/utils/format-user-name";
import UserCell from "@/components/users/user-cell";
import Combobox from "@/components/form/Combobox";
import { ChipToggle } from "@/components/ui/chip-toggle";
import { organizationService } from "@/lib/api/services/organization.service";
import { useDebouncedValue } from "@/hooks/use-debounced-value";
import type { OrgMember } from "@/types/organization.types";

export type PrincipalKind = "user" | "role" | "group";

export interface ApproverPrincipal {
  principalKind: PrincipalKind;
  principalId: string;
  /** Display label — for rendering chips; not sent to BE. */
  label: string;
}

export interface ApproverPickerProps {
  orgId: string;
  value: ApproverPrincipal[];
  onChange: (next: ApproverPrincipal[]) => void;
  disabled?: boolean;
}

type Tab = "user" | "role" | "group";

const TAB_LABELS: Record<Tab, string> = { user: "User", role: "Role", group: "Group" };
const TAB_OPTIONS = (["user", "role", "group"] as Tab[]).map((value) => ({
  value,
  label: TAB_LABELS[value],
}));

function fallbackPrincipalLabel(kind: PrincipalKind) {
  if (kind === "role") return "Unknown role";
  if (kind === "group") return "Unknown group";
  return "Unknown user";
}

function useOrgMembers(orgId: string, search: string) {
  return useQuery({
    queryKey: ["org-members-picker", orgId, search],
    queryFn: () => organizationService.getMembers(orgId, {
      limit: 30,
      ...(search.length >= 3 ? { search } : {}),
    }),
    enabled: !!orgId,
    staleTime: 60_000,
  });
}

export function ApproverPicker({ orgId, value, onChange, disabled }: ApproverPickerProps) {
  const [tab, setTab] = useState<Tab>("user");
  const [q, setQ] = useState("");
  const debouncedQ = useDebouncedValue(q.trim(), 300);
  const { data: membersData, isLoading, isFetching } = useOrgMembers(orgId, debouncedQ);
  const trimmedQ = q.trim();
  const searchSettling = trimmedQ !== debouncedQ;

  const members: OrgMember[] = searchSettling || (trimmedQ.length > 0 && trimmedQ.length < 3)
    ? []
    : membersData?.data ?? [];
  const memberById = new Map(members.map((member) => [member.userId, member]));

  const selectedIds = new Set(value.map((v) => v.principalId));

  const userOptions = members
    .filter((m) => !selectedIds.has(m.userId))
    .map((m) => ({
      value: m.userId,
      searchText: formatUserName(m.user ?? null, { fallback: "Unknown user" }),
      data: m,
    }));

  const handleSelectUser = useCallback((opt: { value: string; data: OrgMember }) => {
    const label = formatUserName(opt.data.user ?? null, { fallback: "Unknown user" });
    onChange([...value, { principalKind: "user", principalId: opt.value, label }]);
    setQ("");
  }, [value, onChange]);

  const remove = (id: string) => onChange(value.filter((v) => v.principalId !== id));

  const ICON: Record<Tab, React.ElementType> = { user: User, role: Shield, group: Users };
  const displayLabel = (principal: ApproverPrincipal) => {
    if (principal.principalKind !== "user") {
      return principal.label && principal.label !== principal.principalId
        ? principal.label
        : fallbackPrincipalLabel(principal.principalKind);
    }
    const member = memberById.get(principal.principalId);
    const fallback = principal.label && principal.label !== principal.principalId
      ? principal.label
      : "Unknown user";
    return formatUserName(member?.user ?? null, { fallback });
  };

  return (
    <div className="flex flex-col gap-3">
      <ChipToggle
        value={tab}
        onChange={(next) => {
          setTab(next);
          setQ("");
        }}
        options={TAB_OPTIONS}
        className="w-full"
      />

      {/* User search */}
      {tab === "user" && (
        <Combobox
          value={q}
          onChange={setQ}
          options={userOptions}
          onSelect={handleSelectUser}
          placeholder="Search org members…"
          disabled={disabled}
          inputClassName="h-9 px-3 pr-9"
          headerSlot={
            trimmedQ.length > 0 && trimmedQ.length < 3 ? (
              <div className="px-3 py-2 text-xs text-[var(--muted-foreground)]">
                Type at least 3 characters
              </div>
            ) : isLoading || isFetching || searchSettling ? (
              <div className="px-3 py-2 text-xs text-[var(--muted-foreground)]">Loading…</div>
            ) : userOptions.length === 0 ? (
              <div className="px-3 py-2 text-xs text-[var(--muted-foreground)]">
                {q ? "No matching members" : "All members already added"}
              </div>
            ) : null
          }
          renderOption={({ option }) => (
            <UserCell user={option.data.user ?? null} size="sm" fallbackText="Unknown user" />
          )}
        />
      )}

      {/* Role / Group — placeholder until org role/group endpoints land */}
      {(tab === "role" || tab === "group") && (
        <div className="rounded-lg border border-dashed border-[var(--border)] px-3 py-2.5 text-xs text-[var(--muted-foreground)]">
          {tab === "role" ? "Role" : "Group"} selection available when org{" "}
          {tab === "role" ? "role" : "group"} management is configured.
        </div>
      )}

      {/* Selected chips */}
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {value.map((principal) => {
            const Icon = ICON[principal.principalKind];
            const label = displayLabel(principal);
            return (
              <span
                key={principal.principalId}
                className="inline-flex max-w-full items-center gap-1.5 rounded-md border border-[var(--border)] bg-[var(--muted)] px-2 py-1 text-xs font-medium text-[var(--foreground)]"
              >
                <Icon className="size-3 text-[var(--muted-foreground)]" />
                <span className="max-w-[180px] truncate">{label}</span>
                {!disabled && (
                  <button
                    type="button"
                    onClick={() => remove(principal.principalId)}
                    className="ml-0.5 text-[var(--muted-foreground)] hover:text-[var(--foreground)]"
                    aria-label={`Remove ${label}`}
                  >
                    <X className="size-3" />
                  </button>
                )}
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default ApproverPicker;
